// ============================================================================
// ARKO Vertragsgenerator — Chat-Slot-Filling (Stream D)
// ----------------------------------------------------------------------------
// Reine, testbare State-Machine für den ARKO-Reiter: erkennt den Wunsch einen
// Vertrag zu erstellen, erfragt die Pflichtfelder (aus dem Schema-Mirror)
// nacheinander und liefert — sobald vollständig — den `fields`-Body für den
// `/webhook/arko-contract-generate`-Endpoint. Getrennt vom LEXCORE-Pfad
// (`rechtsfrage`): die Erkennung verlangt ein klares Erstellungs-Verb.
//
// FRONTEND-MIRROR (Sync-Pflicht): `looksLikeContractRequest` spiegelt die
// serverseitige Heuristik in `scripts/labor/arko-portal-n8n-code.js`
// (Section INTENT_HEURISTIK, CONTRACT_INTENT_RE / CONTRACT_VERB_RE). Bei
// Änderung beide Stellen synchron halten (wie beim rechtsfrage-Mirror in
// InlineChat.tsx dokumentiert).
// ============================================================================

import {
  ContractSchema,
  ContractValues,
  LeafField,
  buildSubmitFields,
  collectLeafFields,
  getByPath,
  getContractTemplate,
  isEffectivelyRequired,
  parseFieldAnswer,
  questionForField,
  setByPath,
} from './contractTemplates';

// Erstellungs-Verb + Vertrags-Substantiv. Bewusst eng, damit Rechtsfragen
// ("Was regelt der Mietvertrag bei Befristung?") NICHT als Erstellungswunsch
// gelten. Spiegel: scripts/labor/arko-portal-n8n-code.js INTENT_HEURISTIK.
const CONTRACT_VERB_RE = /\b(erstell|erzeug|generier|aufsetz|aufzusetzen|anleg|entwerf|entwurf|mach\s+mir|brauche?\s+einen?|braucht\s+einen?|ben[öo]tige)\b/i;
const CONTRACT_NOUN_RE = /\b(miet ?vertrag|mietvertrags|vertrag|vertrags)\b/i;

/** Frontend-Mirror der Server-Heuristik: Wunsch, einen Vertrag zu erstellen? */
export function looksLikeContractRequest(text: string): boolean {
  const t = String(text || '').trim();
  if (!t) return false;
  return CONTRACT_VERB_RE.test(t) && CONTRACT_NOUN_RE.test(t);
}

const CANCEL_RE = /\b(abbrechen|abbruch|stop(p)?|vergiss\s+es|doch\s+nicht|abbreche)\b/i;

export interface ContractFlowState {
  active: boolean;
  templateId: string;
  /** Bereits erfasste, typisierte Werte (nested, wie der Submit-Body). */
  collected: ContractValues;
  /** Pfad des aktuell abgefragten Felds. */
  askingPath: string | null;
}

export const EMPTY_CONTRACT_FLOW: ContractFlowState = {
  active: false,
  templateId: '',
  collected: {},
  askingPath: null,
};

function isMissing(prop: LeafField['prop'], value: unknown): boolean {
  if (prop.type === 'boolean') return typeof value !== 'boolean';
  if (prop.type === 'array') return false; // optional, blockiert nie
  return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
}

/** Erstes effektiv erforderliches, noch fehlendes Blattfeld (in Schema-Reihenfolge). */
export function nextMissingField(schema: ContractSchema, collected: ContractValues): LeafField | null {
  for (const f of collectLeafFields(schema)) {
    if (!isEffectivelyRequired(f, collected)) continue;
    if (isMissing(f.prop, getByPath(collected, f.path))) return f;
  }
  return null;
}

function findLeaf(schema: ContractSchema, path: string): LeafField | undefined {
  return collectLeafFields(schema).find((f) => f.path === path);
}

function buildDisplayName(collected: ContractValues, templateLabel: string): string {
  const mieter = getByPath(collected, 'mieter.name');
  const who = typeof mieter === 'string' && mieter.trim() ? ` — ${mieter.trim()}` : '';
  const stamp = new Date().toISOString().slice(0, 10);
  return `${templateLabel}${who} (${stamp})`;
}

export interface ContractTurnResult {
  state: ContractFlowState;
  botMessages: string[];
  /** Gesetzt, wenn alle Pflichtfelder vorliegen — Aufrufer POSTet diese fields. */
  ready?: { templateType: string; fields: ContractValues; displayName: string };
  /** Flow wurde beendet/abgebrochen. */
  ended?: boolean;
}

/** Startet den Slot-Filling-Flow für einen Vertragstyp. */
export function startContractFlow(templateId: string): ContractTurnResult {
  const tpl = getContractTemplate(templateId);
  if (!tpl) {
    return {
      state: EMPTY_CONTRACT_FLOW,
      botMessages: ['Dieser Vertragstyp ist derzeit nicht verfügbar.'],
      ended: true,
    };
  }
  const collected: ContractValues = {};
  const first = nextMissingField(tpl.schema, collected);
  const intro =
    `Gern — ich helfe Ihnen, einen **${tpl.label}** (Entwurf) zu erstellen. ` +
    `Ich frage die nötigen Angaben nacheinander ab; Sie können jederzeit „abbrechen" schreiben. ` +
    `Hinweis: Entwurfs-Assistent, keine Rechtsberatung (§ 9 RAO).`;
  if (!first) {
    return {
      state: { active: true, templateId, collected, askingPath: null },
      botMessages: [intro],
      ended: true,
    };
  }
  return {
    state: { active: true, templateId, collected, askingPath: first.path },
    botMessages: [intro + '\n\n' + questionForField(first)],
  };
}

/**
 * Verarbeitet eine Nutzer-Antwort im aktiven Flow. Liefert neue Bot-Nachrichten
 * und – wenn vollständig – `ready` mit dem Submit-Body.
 */
export function advanceContractFlow(state: ContractFlowState, userText: string): ContractTurnResult {
  const tpl = getContractTemplate(state.templateId);
  if (!tpl || !state.active) {
    return { state: EMPTY_CONTRACT_FLOW, botMessages: [], ended: true };
  }
  const text = String(userText || '').trim();

  if (CANCEL_RE.test(text)) {
    return {
      state: EMPTY_CONTRACT_FLOW,
      botMessages: ['Alles klar, ich habe den Vertrags-Entwurf abgebrochen. Sie können jederzeit neu starten.'],
      ended: true,
    };
  }

  const schema = tpl.schema;

  // Falls (theoretisch) kein Feld aktiv abgefragt wird: nächstes bestimmen.
  let askingPath = state.askingPath;
  if (!askingPath) {
    const next = nextMissingField(schema, state.collected);
    if (!next) return finalize(state, schema, tpl.label);
    askingPath = next.path;
  }

  const leaf = findLeaf(schema, askingPath);
  if (!leaf) {
    return finalize(state, schema, tpl.label);
  }

  const parsed = parseFieldAnswer(leaf.prop, text);
  if (!parsed.ok) {
    return {
      state: { ...state, askingPath },
      botMessages: [`${parsed.error ?? 'Ungültige Eingabe.'}\n\n${questionForField(leaf)}`],
    };
  }

  const collected = setByPath(state.collected, askingPath, parsed.value);
  const next = nextMissingField(schema, collected);
  if (next) {
    return {
      state: { ...state, collected, askingPath: next.path },
      botMessages: [questionForField(next)],
    };
  }
  return finalize({ ...state, collected, askingPath: null }, schema, tpl.label);
}

function finalize(state: ContractFlowState, schema: ContractSchema, templateLabel: string): ContractTurnResult {
  const fields = buildSubmitFields(schema, state.collected);
  const displayName = buildDisplayName(state.collected, templateLabel);
  return {
    state: { ...state, askingPath: null },
    botMessages: ['Danke — alle Pflichtangaben liegen vor. Ich erstelle jetzt den Vertrags-Entwurf…'],
    ready: { templateType: state.templateId, fields, displayName },
  };
}
